import { Injectable } from "@angular/core";
import { AccountUnitOfWork } from "@data";
import { ProximityPref } from "@models";
import { AuthService } from "./auth.service";

/** Like layoutConfig in apps\prox-ui\src\app\layout\service\layout.service.ts */
export interface LayoutConfig {
  preset: string;
  primary: string;
  surface: string | undefined | null;
  darkTheme: boolean;
  menuMode: string;
  menuTheme: string;
  cardStyle: string;
}

/**
 * Key for layout config in ProximityPref.prefsJson
 * Shared namespace with DashboardConfigService
 */
const LAYOUT_KEY = 'layout';


@Injectable({ providedIn: 'root' })
export class UserPrefsService {
  private configString?: string;
  private config?: LayoutConfig;
  private pref?: ProximityPref;
  gridTheme = "ag-theme-balham";
  private hasLoaded = false;

  constructor(private uow: AccountUnitOfWork, private authService: AuthService) {
  }

  setConfig(config: LayoutConfig) {
    this.setGridTheme(config);
    const json = JSON.stringify(config);
    if (json !== this.configString && this.hasLoaded) {
      this.config = config;
      this.configString = json;

      if (this.pref && this.authService.getUser()) {
        // Preserve other data in prefsJson (e.g., dashboard config)
        let existingData: Record<string, unknown> = {};
        if (this.pref.prefsJson) {
          try {
            const parsed = JSON.parse(this.pref.prefsJson);
            // Handle migration from old format (direct LayoutConfig) to new format (keyed)
            if (parsed && typeof parsed === 'object' && !parsed[LAYOUT_KEY] && parsed.preset) {
              // Old format - entire prefsJson is LayoutConfig, migrate to new format
              existingData = {};
            } else {
              existingData = parsed;
            }
          } catch {
            existingData = {};
          }
        }
        existingData[LAYOUT_KEY] = config;
        this.pref.prefsJson = JSON.stringify(existingData);
      } else {
        const newData = { [LAYOUT_KEY]: config };
        this.pref = this.uow.createEntity(ProximityPref, {
          proximityUserId: this.authService.getUser()?.id,
          prefsJson: JSON.stringify(newData)
        });
      }
      return this.uow.commit();
    }
    return undefined;
  }

  async getConfig() {
    if (!this.config && this.authService.getUser()) {
      const proximityUserId = this.authService.getUser()?.id;
      const prefs = await this.uow.createQuery(ProximityPref).where({ proximityUserId }).execute();
      if (prefs.length) {
        this.pref = prefs[0];
        const s = prefs[0].prefsJson;
        if (s) {
          try {
            const parsed = JSON.parse(s);
            // Handle both old format (direct LayoutConfig) and new format (keyed)
            if (parsed && typeof parsed === 'object') {
              if (parsed[LAYOUT_KEY]) {
                // New format - layout is under 'layout' key
                this.config = parsed[LAYOUT_KEY] as LayoutConfig;
              } else if (parsed.preset) {
                // Old format - entire prefsJson is LayoutConfig
                this.config = parsed as LayoutConfig;
              }
              if (this.config) {
                this.configString = JSON.stringify(this.config);
                this.setGridTheme(this.config);
              }
            }
          } catch {
            // Invalid JSON, ignore
          }
        }
      }
      this.hasLoaded = true;
    }
    return this.config;
  }

  private setGridTheme(config: LayoutConfig) {
    let theme = (config.preset == 'Lara') ? "ag-theme-alpine" : "ag-theme-balham";
    if (config.darkTheme) {
      theme += "-dark";
    }
    this.gridTheme = theme;
  }

}
